// @ts-nocheck
/**
 * @typedef {import('../interfaces/context/IFormularDenuncia.context.interface').IDatosPersonales} IDatosPersonales
 * @typedef {import('../interfaces/context/IFormularDenuncia.context.interface').IDatosPersonalesContext} IDatosPersonalesContext
 * @typedef {import('../interfaces/models/IPersonaImplicada.interface').IPersonaImplicada} IPersonaImplicada
 * @typedef {import('../interfaces/models/IArchivoAdjunto.interface').IArchivoAdjunto} IArchivoAdjunto
 */

import React, { createContext, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { arrayOf, node, shape } from 'prop-types';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toBase64 } from '@ancert/aui-utils';

import { postFormularDenuncia } from '../services/denuncias.service';

import { DOCUMENT_VALIDATION, MAIL_ERROR, PHONE_ERROR, POLICY_REQUIRED, REQUIRED_FIELD, TIPO_DOC_ENUM } from '../constants/common.constants';
import { ROUTES } from '../constants/routes.constants';
import { REGEX_KEY } from '../constants/regex.constants';
import { validateNIE, validateNIF, validatePassport } from '../utils/generic.utils';
import { blobToBase64 } from '../utils/fileUtils';
import { STEPS } from '../lib/formularDenuncia.enum';
import TIPO_ADJUNTO from '../lib/adjuntos.enum';

const datosPersonalesDefaults = {
  isAnonimo: false,
  email: '',
  document: '',
  name: '',
  lastname1: '',
  lastname2: '',
  position: '',
  phone: '',
  address: '',
  documentType: '',
};
/**
 * @type {IDatosPersonales}
 */
const defaultValues = {
  // Datos personales
  ...datosPersonalesDefaults,
  // Personas implicadas y Archivos adjuntos
  reunion: [],
  acceptPolicy: [],
};

const datosPersonalesYup = yup.object().shape({
  isAnonimo: yup.bool(),
  name: yup.string().when('isAnonimo', {
    is: (isAnonimo) => isAnonimo,
    then: yup.string().notRequired(),
    otherwise: yup.string().required(REQUIRED_FIELD),
  }),
  lastname1: yup.string().when('isAnonimo', {
    is: (isAnonimo) => isAnonimo,
    then: yup.string().notRequired(),
    otherwise: yup.string().required(REQUIRED_FIELD),
  }),
  lastname2: yup.string(),
  phone: yup.string().when('isAnonimo', {
    is: (isAnonimo) => isAnonimo,
    then: yup.string().notRequired(),
    otherwise: yup.string().matches(REGEX_KEY.PHONE, { message: PHONE_ERROR, excludeEmptyString: true }),
  }),
  documentType: yup.string().when('isAnonimo', {
    is: (isAnonimo) => isAnonimo,
    then: yup.string().notRequired(),
    otherwise: yup.string().required(REQUIRED_FIELD),
  }),
  document: yup.string().when('isAnonimo', {
    is: (isAnonimo) => isAnonimo,
    then: yup.string().notRequired(),
    otherwise: yup
      .string()
      .required(REQUIRED_FIELD)
      .when('documentType', (documentType, fieldSchema) => {
        const alteredSchema = fieldSchema
          .required(REQUIRED_FIELD)
          .max(25, DOCUMENT_VALIDATION.MAX)
          .min(6, DOCUMENT_VALIDATION.MIN);

        if (documentType === TIPO_DOC_ENUM.PASSPORT)
          return alteredSchema.test('document', DOCUMENT_VALIDATION.PASSPORT, (value) =>
            validatePassport(value));

        if (documentType === TIPO_DOC_ENUM.NIF)
          return alteredSchema.test('document', DOCUMENT_VALIDATION.NIF, (value) =>
            validateNIF(value),
          );
          
        if (documentType === TIPO_DOC_ENUM.NIE)
          return alteredSchema.test('document', DOCUMENT_VALIDATION.NIE, (value) =>
            validateNIE(value),
          );
        return alteredSchema;
      }),
  }),
  email: yup.string().when('isAnonimo', {
    is: (isAnonimo) => isAnonimo,
    then: yup.string().notRequired(),
    otherwise: yup.string().required(REQUIRED_FIELD).email(MAIL_ERROR),
  }),
  position: yup.string(),
  address: yup.string(),
});

const personasArchivoYup = yup.object().shape({
  reunion: yup.array(),
  departamento: yup.string().when('reunion', {
    is: (reunion) => reunion.length === 0,
    then: yup.string().notRequired(),
    otherwise: yup.string().required(REQUIRED_FIELD),
  }),
  acceptPolicy: yup.array().min(1, POLICY_REQUIRED),
});

/**
 *
 * @param {number} step
 * @param {*} comunicacionYup
 * @returns
 */
const resolver = (step, comunicacionYup) => {
  if (step === STEPS.DATOS_COMUNICACION) {
    return yupResolver(comunicacionYup);
  }
  if (step === STEPS.DATOS_PERSONALES) {
    return yupResolver(datosPersonalesYup);
  }

  return yupResolver(personasArchivoYup);
};

/**
 * @type {IDatosPersonalesContext}
 */
const contextDefaultValue = {
  activeStep: STEPS.DATOS_COMUNICACION,
  archivosAdjuntos: [],
  audioAdjuntado: null,
  control: null,
  formState: null,
  personasImplicadas: [],
  addArchivosAdjuntos: null,
  addPersonaImplicada: null,
  adjuntarAudio: null,
  eliminarArchivoAdjunto: null,
  eliminarPersonaImplicada: null,
  goHome: null,
  nFormFieldFilled: null,
  nFormField: null,
  onClickNext: null,
  setActiveStep: null,
  watch: null,
  result: null,
};

const getDataComunicacionObject = (data) => {
  const comunicacionDefaults = {};
  const comunicacionYup = {};

  data.forEach((row, rowIndex) => {
    row.forEach(({ type, required }, colIndex) => {
      const key = `${type}-${rowIndex}-${colIndex}`;
      comunicacionDefaults[key] = '';
      if (required) {
        comunicacionYup[key] = yup.string().required(REQUIRED_FIELD);
      } else {
        comunicacionYup[key] = yup.string();
      }
    });
  });
  return { comunicacionDefaults, comunicacionYup: yup.object().shape(comunicacionYup) };
};

const FormularDenunciaContext = createContext(contextDefaultValue);

/**
 * Componente que genera el Layout
 *
 * @component
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {Array} props.dataComunicacion
 * @returns {React.ReactElement}
 */
const FormularDenunciaProvider = ({ children, dataComunicacion }) => {
  const history = useHistory();
  const [activeStep, setActiveStep] = useState(contextDefaultValue.activeStep);
  const [result, setResult] = useState(contextDefaultValue.result);

  const { comunicacionDefaults, comunicacionYup } = getDataComunicacionObject(dataComunicacion);

  const { control, formState, getValues, handleSubmit, reset, watch } = useForm({
    defaultValues: { ...comunicacionDefaults, ...defaultValues },
    resolver: resolver(activeStep, comunicacionYup),
    mode: 'all',
    reValidateMode: 'onChange',
  });
  const [audio, setAudio] = useState(null);
  const [datosComunicacionForm, setDatosComunicacionForm] = useState([]);
  const [archivosAdjuntos, setArhivosAdjuntos] = useState([]);
  const [personasImplicadas, setPersonasImplicadas] = useState([]);

  /**
   *
   * @param {IArchivoAdjunto} data
   */
  const addArchivosAdjuntos = (data) => {
    let newArchivosAdjuntos = [...archivosAdjuntos];
    if (!data.id) {
      newArchivosAdjuntos.push({ ...data, id: archivosAdjuntos.length + 1 });
    } else {
      newArchivosAdjuntos = [...archivosAdjuntos].map((archivo) => {
        if (archivo.id === data.id) {
          return data;
        }
        return archivo;
      });
    }
    setArhivosAdjuntos(newArchivosAdjuntos);
  };

  /**
   *
   * @param {number} idArchivo
   */
  const eliminarArchivoAdjunto = (idArchivo) => {
    const archivo = [...archivosAdjuntos].find(({ id }) => id === idArchivo);
    if (archivo.type === TIPO_ADJUNTO.MP3) {
      setAudio(null);
    }
    const newArchivosAdjuntos = [...archivosAdjuntos].filter(({ id }) => id !== idArchivo);
    setArhivosAdjuntos(newArchivosAdjuntos);
  };

  /**
   *
   * @param {IPersonaImplicada} data
   */
  const addPersonaImplicada = (data) => {
    let newPersonasImplicadas = [...personasImplicadas];
    if (!data.id) {
      newPersonasImplicadas.push({ ...data, id: personasImplicadas.length + 1 });
    } else {
      newPersonasImplicadas = [...personasImplicadas].map((persona) => {
        if (persona.id === data.id) {
          return data;
        }
        return persona;
      });
    }
    setPersonasImplicadas(newPersonasImplicadas);
  };

  /**
   *
   * @param {number} idPersona
   */
  const eliminarPersonaImplicada = (idPersona) => {
    const newPersonasImplicadas = [...personasImplicadas].filter(({ id }) => id !== idPersona);
    setPersonasImplicadas(newPersonasImplicadas);
  };

  const adjuntarAudio = async (dataAudio) => {
    setAudio(dataAudio);
    if (dataAudio) {
      addArchivosAdjuntos(dataAudio);
      return;
    }

    const newAdjuntos = archivosAdjuntos.filter(({ type }) => type !== TIPO_ADJUNTO.MP3);
    setArhivosAdjuntos(newAdjuntos);
  };

  const goHome = () => {
    reset(defaultValues);
    setAudio(null);
    setPersonasImplicadas([]);
    setArhivosAdjuntos([]);
    setDatosComunicacionForm([]);
    setActiveStep(STEPS.DATOS_COMUNICACION);
    history.push(ROUTES.HOME);
  };

  const getPayload = async (dataForm) => {
    const payload = {};
    const { data } = dataForm;

    if (!data.isAnonimo) {
      const { email, document, name, lastname1, lastname2, position, phone, address, documentType } = data;
      payload.personalData = {
        documentType,
        email,
        document,
        name,
        lastname1,
        lastname2,
        position,
        phone,
        address,
      };
    }

    const complaint = {};
    if (data.reunion?.length > 0) {
      complaint.meeting = data.departamento;
    }

    complaint.data = JSON.stringify(datosComunicacionForm);
    complaint.involved = JSON.stringify(personasImplicadas);

    complaint.attachments = await archivosAdjuntos.reduce(async (previousPromise, archivo) => {
      const acc = await previousPromise;
      const { blob, description, file, name, rawFile } = archivo;
      const finalRawFile = file?.rawFile || rawFile;
      const b64 = blob ? await blobToBase64(blob) : await toBase64(finalRawFile);
      const fileObj = { name, description, size: blob ? blob.size : finalRawFile.size, data: b64.split(',')[1] };
      acc.push(fileObj);
      return acc;
    }, Promise.resolve([]));
    payload.complaint = complaint;
    return payload;
  };

  const enviarDenuncia = async (data) => {
    const payload = await getPayload(data);
    setResult(null);
    const { status, data: response } = await postFormularDenuncia({ data: payload });
    if (status === 201) {
      if (!payload.personalData) {
        setResult(response);
      }
      setActiveStep(activeStep + 1);
    }
  };

  const saveDatosComunicacionForm = (data) => {
    const saveDataComunicacion = dataComunicacion.map((row, rowIndex) =>
      row.map((col, colIndex) => ({ ...col, value: data[`${col.type}-${rowIndex}-${colIndex}`] }))
    );
    setDatosComunicacionForm(saveDataComunicacion);
  };

  const onClickNext = async (data) => {
    if (activeStep === STEPS.DATOS_COMUNICACION) {
      saveDatosComunicacionForm(data);
    }
    if (activeStep < STEPS.PERSONAS_ARCHIVOS) {
      setActiveStep(activeStep + 1);
    }
    if (activeStep === STEPS.PERSONAS_ARCHIVOS) {
      await enviarDenuncia({ data });
    }
  };

  const nFormFieldFilled = () => {
    const values = getValues();
    return Object.keys(comunicacionDefaults).filter((field) => values[field])?.length;
  };

  const nFormField = () => Object.keys(comunicacionDefaults).length;

  const value = {
    activeStep,
    archivosAdjuntos,
    audioAdjuntado: audio,
    control,
    formState,
    personasImplicadas,
    addArchivosAdjuntos,
    addPersonaImplicada,
    adjuntarAudio,
    eliminarArchivoAdjunto,
    eliminarPersonaImplicada,
    goHome,
    nFormFieldFilled,
    nFormField,
    onClickNext: handleSubmit(onClickNext),
    setActiveStep,
    watch,
    result,
  };

  return <FormularDenunciaContext.Provider value={value}>{children}</FormularDenunciaContext.Provider>;
};

FormularDenunciaProvider.propTypes = {
  children: node.isRequired,
  dataComunicacion: arrayOf(arrayOf(shape({}))),
};
FormularDenunciaProvider.defaultProps = {
  dataComunicacion: [],
};

const useFormularDenuncia = () => {
  const context = useContext(FormularDenunciaContext);

  if (context === undefined) {
    throw new Error('useFormularDenuncia must be used within provider');
  }

  return context;
};

export { FormularDenunciaProvider, FormularDenunciaContext, useFormularDenuncia };
